import WebApp from '@twa-dev/sdk';
import i18n from './locales/i18n';
import api, { validateTelegramUser } from './services/api';

type ButtonType = 'default' | 'destructive' | 'ok';

interface PopupButton {
  type: ButtonType;
  text: string;
  id?: string;
}

interface PopupParams {
  title?: string;
  message: string;
  buttons: PopupButton[];
}

class TelegramWebApp {
  private static initialized = false;

  private static updateThemeColors() {
    try {
      const root = document.documentElement;
      const themeParams = WebApp.themeParams;

      if (!themeParams) {
        console.warn('Theme params are not available');
        return;
      }

      // Основные цвета темы
      root.style.setProperty('--tg-theme-bg-color', themeParams.bg_color);
      root.style.setProperty('--tg-theme-text-color', themeParams.text_color);
      root.style.setProperty('--tg-theme-hint-color', themeParams.hint_color);
      root.style.setProperty('--tg-theme-link-color', themeParams.link_color);
      root.style.setProperty('--tg-theme-button-color', themeParams.button_color);
      root.style.setProperty('--tg-theme-button-text-color', themeParams.button_text_color);
      root.style.setProperty('--tg-theme-secondary-bg-color', themeParams.secondary_bg_color);

      // Дополнительные цвета темы
      root.style.setProperty('--tg-theme-header-bg-color', themeParams.header_bg_color);
      root.style.setProperty('--tg-theme-accent-text-color', themeParams.accent_text_color);
      root.style.setProperty('--tg-theme-section-bg-color', themeParams.section_bg_color);
      root.style.setProperty('--tg-theme-section-header-text-color', themeParams.section_header_text_color);
      root.style.setProperty('--tg-theme-subtitle-text-color', themeParams.subtitle_text_color);
      root.style.setProperty('--tg-theme-destructive-text-color', themeParams.destructive_text_color);

      // Цветовая схема
      root.style.setProperty('--tg-color-scheme', WebApp.colorScheme);

      // Применяем основные цвета к body
      document.body.style.backgroundColor = `var(--tg-theme-bg-color)`;
      document.body.style.color = `var(--tg-theme-text-color)`;

      console.log('Theme colors updated successfully:', {
        themeParams,
        colorScheme: WebApp.colorScheme
      });
    } catch (error) {
      console.error('Error updating theme colors:', error);
    }
  }

  static async init() {
    // Проверяем, не была ли уже выполнена инициализация
    if (this.initialized) {
      console.log('[TelegramWebApp] Already initialized');
      return true;
    }

    try {
      // Проверяем наличие initData
      if (!this.isReady()) {
        throw new Error('Telegram WebApp not initialized properly');
      }

      // Базовая инициализация
      if (!WebApp.isExpanded) {
        await WebApp.ready();
        await WebApp.expand();
        WebApp.requestFullscreen();
      }

      // Обновляем тему после инициализации
      this.updateThemeColors();

      // Слушаем изменения темы
      WebApp.onEvent('themeChanged', () => {
        this.updateThemeColors();
      });

      // Базовые настройки
      WebApp.enableClosingConfirmation();
      WebApp.disableVerticalSwipes();
      
      // Валидируем пользователя
      try {
        await validateTelegramUser();
        console.log('[TelegramWebApp] User validated successfully');
      } catch (error) {
        console.error('[TelegramWebApp] Failed to validate user:', error);
        // Не показываем попап здесь - ошибка уже обработана в validateTelegramUser
        // и может быть показана в других местах
        return false;
      }

      // Загружаем язык пользователя
      try {
        const userId = this.initDataUnsafe?.user?.id;
        if (userId) {
          const response = await api.get<{ language_code?: string }>(`/api/v1/users/${userId}`);
          const user = response.data;
          if (user?.language_code) {
            await i18n.changeLanguage(user.language_code);
            console.log('[TelegramWebApp] Language loaded:', user.language_code);
          }
        }
      } catch (error) {
        console.warn('[TelegramWebApp] Failed to load user language:', error);
        // Не критичная ошибка, продолжаем инициализацию
      }
      
      this.initialized = true;
      console.log('[TelegramWebApp] Initialization completed successfully');
      return true;
    } catch (error) {
      console.error('[TelegramWebApp] Initialization failed:', error);
      return false;
    }
  }

  /**
   * @deprecated Используйте validateTelegramUser из services/api.ts
   * Этот метод оставлен для обратной совместимости, но теперь делегирует вызов API сервису
   */
  static async validateUser(): Promise<boolean> {
    try {
      await validateTelegramUser();
      return true;
    } catch (error) {
      console.error('[TelegramWebApp] Validation error:', error);
      return false;
    }
  }

  static async requestLocationAndTimezone(): Promise<{
    timezone: string;
  }> {
    try {
      return {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
    } catch (error) {
      console.error('Error in requestLocationAndTimezone:', error);
      return {
        timezone: 'UTC'
      };
    }
  }

  // Методы управления приложением
  static enableClosingConfirmation() {
    WebApp.enableClosingConfirmation();
  }

  static disableClosingConfirmation() {
    WebApp.disableClosingConfirmation();
  }

  static disableVerticalSwipes() {
    WebApp.disableVerticalSwipes();
  }

  static enableVerticalSwipes() {
    WebApp.enableVerticalSwipes();
  }

  static requestFullscreen() {
    WebApp.requestFullscreen();
  }

  // UI методы
  static showAlert(message: string) {
    WebApp.showAlert(message);
  }

  static showConfirm(message: string) {
    WebApp.showConfirm(message);
  }

  static showPopup(params: PopupParams) {
    // Convert 'ok' type to 'default' for Telegram API compatibility
    const buttons = params.buttons.map(button => ({
      ...button,
      type: button.type === 'ok' ? 'default' as const : button.type
    }));

    WebApp.showPopup({
      ...params,
      buttons
    });
  }

  // Методы для работы с главным экраном
  static addToHomeScreen() {
    WebApp.addToHomeScreen();
  }

  static close() {
    WebApp.close();
  }

  // Методы для работы с данными
  static sendData(data: string) {
    WebApp.sendData(data);
  }

  static requestWriteAccess(callback: (success: boolean) => void) {
    WebApp.requestWriteAccess(callback);
  }

  // Методы для работы с initData и аутентификацией
  static getInitData(): string {
    try {
      // @ts-expect-error - WebApp.initData существует, но нет в типах
      const initData = window.Telegram?.WebApp?.initData || WebApp.initData;
      
      if (!initData) {
        throw new Error('No initData available');
      }

      // Проверяем, что initData содержит все необходимые параметры
      const params = new URLSearchParams(initData);
      const requiredParams = ['auth_date', 'user', 'hash'];
      const hasAllParams = requiredParams.every(param => params.has(param));
      
      if (!hasAllParams) {
        throw new Error('Missing required parameters in initData');
      }

      return initData;
    } catch (error) {
      console.error('[TelegramWebApp] Error getting initData:', error);
      throw error;
    }
  }

  static isReady(): boolean {
    return Boolean(WebApp.initData);
  }

  static getUserData() {
    return window.Telegram?.WebApp?.initDataUnsafe?.user || null;
  }

  static get initDataUnsafe() {
    return WebApp.initDataUnsafe;
  }

  static get colorScheme() {
    return WebApp.colorScheme;
  }

  static get themeParams() {
    return WebApp.themeParams;
  }

  static get isExpanded() {
    return WebApp.isExpanded;
  }

  static get viewportHeight() {
    return WebApp.viewportHeight;
  }

  static get viewportStableHeight() {
    return WebApp.viewportStableHeight;
  }

  static get headerColor() {
    return WebApp.headerColor;
  }

  static get backgroundColor() {
    return WebApp.backgroundColor;
  }

  static get platform() {
    return WebApp.platform;
  }

  static get version() {
    return WebApp.version;
  }

  static HapticFeedback = {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => {
      WebApp.HapticFeedback.impactOccurred(style);
    },
    notificationOccurred: (type: 'error' | 'success' | 'warning') => {
      WebApp.HapticFeedback.notificationOccurred(type);
    },
    selectionChanged: () => {
      WebApp.HapticFeedback.selectionChanged();
    }
  };
}

export default TelegramWebApp;